// libraries
import React from 'react'
import { forwardRef, useRef, useEffect, useImperativeHandle, useState } from 'react'

// svg
import { GeneralSpeaker, GeneralSpeakerOff } from '/react4xp/components/Svg/Icons/General'

// types
export interface ReelsCardProps {
	youtube?: string
	vimeo?: string
	videoAutoplay?: boolean
	title?: string
	onMouseEnter?: () => void
	lazyLoadVideo?: boolean
}

export interface ReelsCardRef {
	playVideo: () => void
	pauseVideo: () => void
	hasVideo: () => boolean
}

type UnifiedPlayer = {
	play: () => void
	pause: () => void
	mute: () => void
	unmute: () => void
	destroy: () => void
}

let youtubeApiPromise: Promise<void> | null = null
let vimeoApiPromise: Promise<void> | null = null

const safePlayerCall = (instance: any, methodName: string) => {
	const method = instance?.[methodName]
	if (typeof method === 'function') {
		method.call(instance)
	}
}

const parseVimeoId = (value?: string) => {
	if (!value) return null

	if (/^\d+$/.test(value)) {
		return Number(value)
	}

	const match = value.match(/vimeo\.com\/(?:video\/)?(\d+)/)
	return match ? Number(match[1]) : null
}

const loadYoutubeApi = () => {
	if ((window as any).YT?.Player) return Promise.resolve()
	if (youtubeApiPromise) return youtubeApiPromise

	youtubeApiPromise = new Promise((resolve, reject) => {
		const existingScript = document.getElementById('youtube-iframe-api') as HTMLScriptElement | null
		const handleReady = () => resolve()

		if ((window as any).YT?.Player) {
			resolve()
			return
		}

		const previousReady = (window as any).onYouTubeIframeAPIReady
		;(window as any).onYouTubeIframeAPIReady = () => {
			if (previousReady) previousReady()
			handleReady()
		}

		if (!existingScript) {
			const script = document.createElement('script')
			script.id = 'youtube-iframe-api'
			script.src = 'https://www.youtube.com/iframe_api'
			script.onerror = () => reject(new Error('Failed to load YouTube iframe API'))
			document.body.appendChild(script)
		}
	})

	return youtubeApiPromise
}

const loadVimeoApi = () => {
	if ((window as any).Vimeo?.Player) return Promise.resolve()
	if (vimeoApiPromise) return vimeoApiPromise

	vimeoApiPromise = new Promise((resolve, reject) => {
		const existingScript = document.getElementById('vimeo-player-api') as HTMLScriptElement | null

		if ((window as any).Vimeo?.Player) {
			resolve()
			return
		}

		const handleLoad = () => {
			if ((window as any).Vimeo?.Player) {
				resolve()
				return
			}
			reject(new Error('Failed to initialize Vimeo player API'))
		}

		if (existingScript) {
			existingScript.addEventListener('load', handleLoad, { once: true })
			existingScript.addEventListener('error', () => reject(new Error('Failed to load Vimeo player API')), { once: true })
			return
		}

		const script = document.createElement('script')
		script.id = 'vimeo-player-api'
		script.src = 'https://player.vimeo.com/api/player.js'
		script.onload = handleLoad
		script.onerror = () => reject(new Error('Failed to load Vimeo player API'))
		document.body.appendChild(script)
	})

	return vimeoApiPromise
}

const ReelsCard = forwardRef<ReelsCardRef, ReelsCardProps>(({
	youtube,
	vimeo,
	videoAutoplay,
	title,
	onMouseEnter
}, ref) => {
	const playerRef = useRef<UnifiedPlayer | null>(null)
	const mediaRef = useRef<HTMLDivElement>(null)
	const divRef = useRef<HTMLDivElement>(null)
	const resolvedYoutube = youtube
	const resolvedVimeo = resolvedYoutube ? undefined : vimeo
	const hasVideo = !!(resolvedYoutube || resolvedVimeo)

	const [isMuted, setIsMuted] = useState(true)
	const [isPaused, setIsPaused] = useState(true)
	const [shouldLoadPlayer, setShouldLoadPlayer] = useState(false)

	/* ---------------------------------------------------------------------
	 * Exposed imperative API
	 * ------------------------------------------------------------------- */
	useImperativeHandle(ref, () => ({
		playVideo: () => {
			if (hasVideo && playerRef.current) {
				playerRef.current.play()
				setIsPaused(false)
			}
		},
		pauseVideo: () => {
			if (hasVideo && playerRef.current) {
				playerRef.current.pause()
				setIsPaused(true)
			}
		},
		hasVideo: () => hasVideo
	}), [hasVideo])

	/* ---------------------------------------------------------------------
	 * IntersectionObserver – decide when to load player
	 * ------------------------------------------------------------------- */
	useEffect(() => {
		if (!hasVideo || !mediaRef.current) return

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setShouldLoadPlayer(true)
					observer.disconnect()
				}
			},
			{
				root: null,
				rootMargin: '200px',
				threshold: 0.25
			}
		)

		observer.observe(mediaRef.current)

		return () => observer.disconnect()
	}, [hasVideo])

	/* ---------------------------------------------------------------------
	 * Player creation (lazy): YouTube or Vimeo
	 * ------------------------------------------------------------------- */
	useEffect(() => {
		if (!hasVideo || !shouldLoadPlayer || !mediaRef.current) return

		let isMounted = true
		const initializePlayer = async () => {
			if (!mediaRef.current) return

			if (resolvedYoutube) {
				await loadYoutubeApi()
				if (!isMounted || !mediaRef.current) return

				const ytPlayer = new (window as any).YT.Player(
					mediaRef.current,
					{
						videoId: resolvedYoutube,
						host: 'https://www.youtube-nocookie.com',
						playerVars: {
							origin: window.location.origin,
							autoplay: 0,
							controls: 0,
							modestbranding: 1,
							rel: 0,
							fs: 0,
							iv_load_policy: 3,
							disablekb: 1,
							loop: 1,
							playlist: resolvedYoutube
						},
						events: {
							onReady: (event: any) => {
								event.target.mute()
								setIsMuted(true)
								setIsPaused(true)

								if (videoAutoplay) {
									event.target.playVideo()
									setIsPaused(false)
								}
							},
							onStateChange: (event: any) => {
								if (event.data === 1) setIsPaused(false)
								if (event.data === 2 || event.data === 0) setIsPaused(true)
							}
						}
					}
				)

				playerRef.current = {
					play: () => safePlayerCall(ytPlayer, 'playVideo'),
					pause: () => safePlayerCall(ytPlayer, 'pauseVideo'),
					mute: () => safePlayerCall(ytPlayer, 'mute'),
					unmute: () => safePlayerCall(ytPlayer, 'unMute'),
					destroy: () => safePlayerCall(ytPlayer, 'destroy')
				}
				return
			}

			const vimeoId = parseVimeoId(resolvedVimeo)
			if (!vimeoId) return

			await loadVimeoApi()
			if (!isMounted || !mediaRef.current) return

			const vimeoPlayer = new (window as any).Vimeo.Player(
				mediaRef.current,
				{
					id: vimeoId,
					autoplay: false,
					controls: false,
					loop: true,
					muted: true,
					byline: false,
					title: false,
					portrait: false,
					dnt: true
				}
			)

			vimeoPlayer.on('loaded', async () => {
				try {
					await vimeoPlayer.setVolume(0)
					setIsMuted(true)
					setIsPaused(true)

					if (videoAutoplay) {
						await vimeoPlayer.play()
						setIsPaused(false)
					}
				} catch {
					// keep UI state unchanged if browser blocks autoplay
				}
			})

			vimeoPlayer.on('play', () => setIsPaused(false))
			vimeoPlayer.on('pause', () => setIsPaused(true))
			vimeoPlayer.on('ended', () => setIsPaused(true))

			playerRef.current = {
				play: () => { void vimeoPlayer.play() },
				pause: () => { void vimeoPlayer.pause() },
				mute: () => { void vimeoPlayer.setVolume(0) },
				unmute: () => { void vimeoPlayer.setVolume(1) },
				destroy: () => { void vimeoPlayer.destroy() }
			}
		}

		void initializePlayer()

		return () => {
			isMounted = false
			playerRef.current?.destroy()
			playerRef.current = null
		}
	}, [hasVideo, shouldLoadPlayer, videoAutoplay, resolvedYoutube, resolvedVimeo])

	const toggleMute = (e: React.MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()

		if (hasVideo && playerRef.current) {
			if (isMuted) {
				playerRef.current.unmute()
				setIsMuted(false)
			} else {
				playerRef.current.mute()
				setIsMuted(true)
			}
		}
	}

	/* ---------------------------------------------------------------------
	 * Render
	 * ------------------------------------------------------------------- */
	return (
		<div
			ref={divRef}
			onMouseEnter={onMouseEnter}
			className='reels-card'
		>
			<button
				type='button'
				className='audio-toggle'
				onClick={toggleMute}
				aria-label={isMuted || isPaused ? 'Unmute audio' : 'Mute audio'}
			>
				{(isMuted || isPaused) ? (
					<GeneralSpeakerOff />
				) : (
					<GeneralSpeaker />
				)}
			</button>

			{hasVideo && (
				<div
					className='reels-card__media-wrapper cover'
					ref={mediaRef}
				/>
			)}

			<div className='reels-card__bottom'>
				<p className='body white'>
					{title}
				</p>
			</div>

		</div>
	)
})

ReelsCard.displayName = 'ReelsCard'
export default ReelsCard